import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import { CategoryFormData, CategorySchema } from "@utils/interface/form.schema";
import { IndividualQuery, METHODS } from "@utils/interface/index.interface";
import { CategoryData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
interface FormProps {
  refetch?: () => void;
}
const CategoryForm: React.FC<FormProps> = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");

  const {
    handleSubmit,
    control,
    setValue,

    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    mode: "onChange",
    resolver: zodResolver(CategorySchema),
  });
  console.log("🚀 ~ errors:", errors);
  /* */
  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<CategoryData>
  >({
    endpoint: endpoint["forms-category"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;

      if (firstResult) {
        if (modalMode === "add") {
          return reset({});
        } else {
          reset(() => ({
            name: firstResult?.name || "",
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (data: any) => {
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
    }
    return {
      data,
      endpoint: endpoint["forms-category"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: CategoryData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      queryClient.invalidateQueries({ queryKey: ["journey-section-listing"] });

      showToast({
        description: `Journey ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    setIsFormPending(false);
    throwErrorAlert(error);
  };

  const { mutate, isPending } = usePostData();
  console.log("errors", errors);
  const onSubmit = (data: CategoryFormData) => {
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "name",
      label: "Category Name",
      colSpan: 12,
      type: "text",
      placeholder: "Enter name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value:
        isPending || (fetchPending && modalItem)
          ? `processing...`
          : `${modalMode || ""} ${(type || pathname)?.replace("-", " ")}`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",
      endIcon: isPending || (fetchPending && modalItem) ? <Spinner /> : null,
      disabled: isPending || (fetchPending && modalItem) ? true : false,
      visable: true,
    },
  ];

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default CategoryForm;
